import queryString from 'querystring';

import { Backend } from './backend';
import { getForm } from './forms';

/**
 * Contains a url property to an image
 */
export interface Image {
  url: string;
}

export interface ProductImage extends Image {
  thumb: Image;
  productthumb?: Image;
}

export interface HeroImage extends Image {
  thumb: Image;
}

interface Alternatives_Attribute {
  article_id: number;
  available_end_at?: Date;
  available_start_at?: Date;
  external_url?: string;
  external_label?: string;
  form_id?: number;
  id: number;
  image: ProductImage;
  pos: number;
  price: number;
  status: 'active' | string;
  stock_status?: StockStatus;
  stock_message?: string;
  title: string;
  ptitle?: string;
  total_price: number;
  vat_rate: number;
  min_per_order: number;
  remaining_for_online: number;
}

export interface Variant {
  alternatives_attributes?: Alternatives_Attribute[];
  article_id: number;
  available_end_at?: Date;
  available_start_at?: Date;
  created_at?: Date;
  external_url?: string;
  external_label?: string;
  id: number;
  image: ProductImage;
  pos: number;
  price: number;
  status: 'active' | string;
  stock_status?: StockStatus;
  stock_message?: string /* implicitly i18n'd through the request */;
  // ticket_info: string;
  // ticket_logo: Image;
  title: string /* implicitly i18n'd through the request */;
  total_price: number;
  updated_at: Date;
  vat_rate: number;
  remaining_for_online: number;
  min_per_order: number;
  package_membership_groups_attributes?: IndexedPackageMembershipGroups;
}

/**
 * 0 = white (unspecified)
 * 1 = green (available)
 * 2 = yellow (in delivery)
 * 3 = red (out of stock)
 * 4 = black (unavailable)
 */
export enum StockStatus {
  UNSPECIFIED = 0,
  AVAILABLE = 1,
  IN_DELIVERY = 2,
  OUT_OF_STOCK = 3,
  UNAVAILABLE = 4,
}
// export type StockStatus = 0 | 1 | 2 | 3 | 4;

export type Variants = Variant[];

export interface GalleryImage {
  gallery: string;
  original?: string;
  productthumb?: string;
  thumb: string;
}

// interface SponsorLogo extends Image {
//   ticket_version: Image;
//   thumb: Image;
// }

export type ArticleType =
  | 'Product'
  | 'ValueCouponProduct'
  | 'SeasonTicket'
  | 'GuestListAsset'
  | 'Fee'
  | 'Ticket'
  | 'Package'
  | 'Upgrade';

export type SoldOutStatus =
  | 'cancelled'
  | 'other1'
  | 'other2'
  | 'other3'
  | 'other4'
  | 'rescheduled_active'
  | 'rescheduled'
  | 'sold_out'
  | 'starts_soon';

/**
 * An article is the common denominator for all sellable things in TT
 */
export interface Article {
  brand_ids?: number[];
  category_ids?: number[];
  event_id?: number;
  free_image_1: Image;
  free_image_2: Image;
  free_image_3: Image;
  free_image_4: Image;
  free_text_1?: string;
  free_text_2?: string;
  free_text_3?: string;
  free_text_4?: string;
  id: number;
  image_thumb: string; // Base64 encoded preview image
  image: ProductImage;
  is_option?: boolean;
  location_id?: number;
  merchant_id?: number;
  min_price?: number;
  max_price?: number;
  has_plan?: boolean;
  sold_out_status?: SoldOutStatus;
  sold_out_status_text?: string; // translated string representation of the sold_out_status
  subtitle?: string;
  supertitle?: string;
  tag_ids?: number[];
  time_begin?: string;
  time_open?: string;
  title: string;
  ptitle: string;
  type: ArticleType;
  /**
   * Relative or absolute URL to the final product
   *
   * If absolute the product might be somewhere external and should be kicked in a new tab.
   * If relative you could use the react-router to navigate on the current App.
   */
  redirect?: string;
  valid_end_on?: Date;
  valid_start_on?: Date;
}

export type Articles = Article[];

export interface Product extends Article {
  available_end_at?: Date;
  available_start_at?: Date;
  description: string;
  eid: string;
  hero_image: HeroImage;
  info_url: string;
  // sponsor_logo_1?: SponsorLogo;
  // sponsor_logo_2?: SponsorLogo;
  // sponsor_logo_3?: SponsorLogo;
  // sponsor_logo_4?: SponsorLogo;
  // sponsor_logo_5?: SponsorLogo;
  // sponsor_logo_6?: SponsorLogo;
  // sponsor_logo_7?: SponsorLogo;
  gallery_image_urls: GalleryImage[];
  status: 'active' | string;
  online_variants_attributes: Variants;
}

export interface PackageMembershipGroup {
  // Membership Group ID
  id: number;
  description: string;
  variants_attributes: IndexedPackageMembershipGroupVariants;
}

export interface IndexedPackageMembershipGroups {
  [groupId: string]: PackageMembershipGroup;
}

export interface IndexedPackageMembershipGroupVariants {
  [variantId: string]: PackageMembershipGroupVariant;
}

export interface PackageMembershipGroupVariant {
  id: number;
  article_id: number;
  full_title: string;
  title: string;
  image: ProductImage;
  remaining_for_online: number;
}

export type Products = Product[];

export interface IndexedProducts {
  [productId: number]: Product | Ticket;
}
/**
 * A ticket is just a product with a few extras
 */
export interface Ticket extends Product {
  ticket_info: string;
  ticket_logo: Image;
}

export type Tickets = Ticket[];

export interface IndexedTickets {
  [ticketId: number]: Ticket;
}

export interface ProductFilter {
  type?: ArticleType | ArticleType[];
  brand_ids?: number | number[];
  category_ids?: number | number[];
  merchant_ids?: number | number[];
  location_ids?: number | number[];
  tag_ids?: number | number[];
  [key: string]: number | number[] | string | string[];
}

export interface Category {
  description?: string;
  hero_image_author?: string;
  hero_image: HeroImage;
  id: number;
  image_author?: string;
  image: Image;
  parent_id?: number;
  position: number;
  show_in_menu: boolean;
  sid: string;
  // status: string;
  subtitle?: string;
  title: string;
}

export type Categories = Category[];

export interface IndexedCategories {
  [categoryId: number]: Category;
}

export interface LocationType {
  active: boolean;
  city: string;
  description: string;
  header_template: string;
  hero_image_author?: string;
  hero_image: HeroImage;
  id: number;
  image_author?: string;
  image: Image;
  latitude: number;
  logo_author?: string;
  logo: Image;
  longitude: number;
  parent_id?: number;
  preposition: string;
  principal: boolean;
  sid: string;
  street: string;
  ticket_info: string;
  title: string;
  url: string;
  zip_code: string;
}
export type Locations = LocationType[];

export interface IndexedLocations {
  [locationId: number]: LocationType;
}

export interface Brand {
  id: number;
  sid: string;
  title: string;
  image: Image;
  description: string;
  hero_image: string;
  /**
   * resembles the importance (the higher the more important)
   */
  feature: number;
  image_author: string;
  hero_image_author: string;
}
export type Brands = Brand[];

export interface IndexedBrands {
  [brandId: number]: Brand;
}

export interface Tag {
  id: number;
  title: string;
}

export type Tags = Tag[];

export interface IndexedTags {
  [tagId: number]: Tag;
}

export interface Event {
  id: number;
  sid: string;
  title: string;
  description: string;
  info_url: string;
  image: string;
  hero_image: HeroImage;
  image_author: string;
  hero_image_author: string;
  subtitle: string;
  supertitle: string;
}

export type Events = Event[];

export interface IndexedEvents {
  [eventId: number]: Event;
}

export interface Merchant {
  id: number;
  sid: string;
  title: string;
  address_addition: string;
  street: string;
  zip_code: string;
  city: string;
  phone: string;
  fax: string;
  email: string;
  website: string;
  description: string;
  tnc: string;
  shipping_text: string;
  imprint_text: string;
  data_privacy_statement: string;
  logo: Image;
  image: Image;
  preposition: string;
  latitude: number;
  longitude: number;
  feature: number; // resembles the importance (the higher the more important)
  first_name: string;
  last_name: string;
  active: boolean;
  shipping_cost: number;
  free_shipping_from: string;
  hero_image: HeroImage;
  needs_tncs: boolean;
  image_author: string;
  hero_image_author: string;
  logo_author: string;
}
export type Merchants = Merchant[];

export interface IndexedMerchants {
  [merchantId: number]: Merchant;
}

export async function getProducts(filter?: ProductFilter, options?: RequestInit) {
  // Convert keys from array-filters to the brackets-notation `name[]` to please rails
  const pimpedFilter = Object.fromEntries(
    Object.entries(filter).map(([key, value]) => {
      return [Array.isArray(value) ? key + '[]' : key, value];
    })
  );
  const qs = queryString.stringify(pimpedFilter);
  return Backend.get<Articles>(`/catalog/products${qs ? `?${qs}` : ''}`, options);
}

export async function getProduct(productId: number, options?: RequestInit) {
  return Backend.get<Product | Ticket>(`/catalog/products/${encodeURIComponent(productId)}`, options);
}

export async function getBrands(options?: RequestInit) {
  return Backend.get<Brands>(`/catalog/brands`, options);
}

export async function getCategories(options?: RequestInit) {
  return Backend.get<Categories>(`/catalog/categories`, options);
}

export async function getTags(options?: RequestInit) {
  return Backend.get<Tags>(`/catalog/tags`, options);
}

export async function getEvents(options?: RequestInit) {
  return Backend.get<Events>(`/catalog/events`, options);
}

export async function getLocations(options?: RequestInit) {
  return Backend.get<Locations>(`/catalog/locations`, options);
}

export async function getMerchants(options?: RequestInit) {
  return Backend.get<Merchants>(`/catalog/merchants`, options);
}

export async function getProductForm(formId: number, options?: RequestInit) {
  return getForm(formId, options);
}
